import { useCallback, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import { PAGE_TYPE_SAMPLE_ITINERARIES } from 'constants/index';
import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import { showSnackbar } from 'hooks/useSnackbar';
import { getSearchFiltersThunk } from 'store/admin/pagesTable/thunks';
import { accountIdSelector, isOwnerSelector, isSuperAdminSelector } from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';
import axiosApi from 'utils/axios';

import { useContentLibraryContext } from '../../context';
import { SEARCH_FILTER_SHOW_STATUS } from './constants';

export function useDashboardSearchFilter({ filter, table, tableName }) {
  const dispatch = useDispatch();
  const accountId = useSelector(accountIdSelector);
  const { handleAfterSaveFilter } = useContentLibraryContext();
  const [loading, setLoading] = useState(false);
  const isOwner = useSelector(isOwnerSelector);
  const isSuperAdmin = useSelector(isSuperAdminSelector);

  const showRadioButtons = useMemo(
    () => !(isOwner || (isSuperAdmin && table === PAGE_TYPE_SAMPLE_ITINERARIES)),
    [isOwner, isSuperAdmin, table],
  );
  const defaultFilter = useMemo(() => getDefaultFilter(table), [table]);
  const restoredFilter = useMemo(() => filter || getDefaultFilter(table, filter), [table, filter]);

  const handleDelete = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      confirmAlert({
        title: 'Are you sure you want to delete this filter?',
        onConfirm: async () => {
          try {
            await axiosApi.delete(`/acc/${accountId}/search_filters/${filter.id}`);
            await dispatch(getSearchFiltersThunk());

            handleAfterSaveFilter(table);
          } catch (err) {
            showSnackbar({ text: GENERAL_ERROR_MESSAGE, type: SNACKBAR_SKIN.ERROR });
          }
        },
      });
    },
    [accountId, filter, handleAfterSaveFilter, table, dispatch],
  );

  const handleSave = useCallback(
    async (newFilter) => {
      const method = newFilter.id ? 'patch' : 'post';
      const tail = newFilter.id ? `/${newFilter.id}` : '';

      try {
        setLoading(true);
        await axiosApi[method](`/acc/${accountId}/search_filters${tail}`, newFilter);
        await dispatch(getSearchFiltersThunk());

        showSnackbar({ text: `Filters for ${tableName} have been saved.` });

        handleAfterSaveFilter(table);
      } catch (err) {
        showSnackbar({ text: GENERAL_ERROR_MESSAGE, type: SNACKBAR_SKIN.ERROR });
      } finally {
        setLoading(false);
      }
    },
    [accountId, table, tableName, handleAfterSaveFilter, dispatch],
  );

  return {
    loading,
    title: `Filter ${tableName}`,
    defaultFilter,
    restoredFilter,
    showRadioButtons,
    handleDelete,
    handleSave,
  };
}

function getDefaultFilter(table, filter = {}) {
  return {
    ...filter,
    data: {
      show: SEARCH_FILTER_SHOW_STATUS.ALL,
      tags: [],
    },
    dashboard: 'content_library',
    table,
  };
}
